import React, { useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Button, Card, CardBody, Textarea } from "@heroui/react";
import { Icon } from "@iconify/react";
import useStore from "../store/useStore";
import { createSolicitation } from "../services/api";
import { ICreateSolicitation } from "../lib/types";
import { toast } from "react-toastify";
import { CameraFullscreen } from "../components/CameraFullscreen";
import { getLogoBase64 } from "../utils/themeManager";

interface FileState {
  name: string;
  originalName: string;
  uri: string | { name: string; uri: string; type: string };
  type: string;
  editing: boolean;
  tempName?: string;
}
const isWebView = () => {
  const userAgent =
    navigator.userAgent || navigator.vendor || (window as any).opera;
  // iOS WebView
  const isIOSWebView =
    /\b(iPhone|iPod|iPad)\b/.test(userAgent) && !/Safari/.test(userAgent);

  // Android WebView
  const isAndroidWebView = /\bwv\b/.test(userAgent) || /; wv\)/.test(userAgent);

  return isIOSWebView || isAndroidWebView;
};

// No WebView Android, o seletor nativo de arquivo (onShowFileChooser) nem sempre abre a câmera
// via input[capture]. Usar o CameraFullscreen (getUserMedia) evita depender dele.
const isAndroidWebViewUA = () => {
  const userAgent =
    navigator.userAgent || navigator.vendor || (window as any).opera;
  return /\bwv\b/.test(userAgent) || /; wv\)/.test(userAgent);
};

export function NewMessagePage() {
  const history = useHistory();
  const location: any = useLocation();
  const [subject, setSubject] = React.useState("");
  const [message, setMessage] = React.useState("");
  const store = useStore();
  const [files, setFiles] = React.useState<FileState[]>([]);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const imageInputRef = React.useRef<HTMLInputElement>(null);
  const [cameraOpen, setCameraOpen] = React.useState(false);
  const { authUser, selectedCompany } = store;
  const [isSending, setIsSending] = useState(false);

  React.useEffect(() => {
    if (location.state?.files) {
      setFiles(location.state.files);
    }
  }, [location.state?.files]);

  // Ao focar no campo mensagem, atribui o assunto automaticamente
  // const handleMessageFocus = React.useCallback(() => {
  //   if (!subject && message.trim().length > 0) {
  //     const words = message.trim().split(/\s+/);
  //     if (words.length > 4) {
  //       setSubject(words.slice(0, 4).join(" ") + " ...");
  //     } else {
  //       setSubject(message.trim());
  //     }
  //   }
  // }, [subject, message]);

  const handleCamera = () => {
    if (isAndroidWebViewUA()) {
      // Android: abre via CameraFullscreen (getUserMedia) em vez do seletor nativo de arquivo
      setCameraOpen(true);
      return;
    }

    if (isWebView()) {
      // Temporariamente adiciona o atributo capture=environment para abrir a câmera
      if (imageInputRef.current) {
        imageInputRef.current.setAttribute("capture", "environment");
        imageInputRef.current.click();
        // Remove o atributo após clicar para não afetar o comportamento da galeria
        setTimeout(() => {
          imageInputRef.current?.removeAttribute("capture");
        }, 500);
      }
    } else {
      setCameraOpen(true);
    }
  };

  const handleGaleria = () => {
    console.log("Botão de galeria clicado");
    imageInputRef.current?.click();
  };

  const handleArquivo = () => {
    console.log("Botão de arquivo clicado");
    fileInputRef.current?.click();
  };

  const handleMessage = (message: React.SetStateAction<string>) => {
    setMessage((prev) => {
      const newMessage =
        typeof message === "function" ? message(prev) : message;

      if (newMessage.trim().length > 0) {
        const words = newMessage.trim().split(/\s+/);
        if (words.length > 4) {
          setSubject(words.slice(0, 4).join(" ") + " ...");
        } else {
          setSubject(newMessage.trim());
        }
      }
      return newMessage;
    });
  };

  const sendMessage = async () => {
    if (isSending) return;
    setIsSending(true);

    const payload: ICreateSolicitation = {
      empresa_id: store.selectedCompany!.id,
      mensagem: message,
      files: files.map((item) => ({
        name: item.tempName ?? item.name,
        type: item.type,
        uri: item.uri,
        base64: (item as any)?.base64,
      })),
      id_destinatario: store.authUser?.bpo!.id,
      destino: store.authUser?.bpo!.empresa!,
      tipo_destinatario: "cliente",
      id_origem: store.authUser?.contato!.id,
      origem: store.authUser?.contato!.nome!,
      tipo_origem: "contato",
      resposta: 0,
      via_app: 1,
      assunto: subject,
    };

    if (!navigator.onLine) {
      const offlineMessages = JSON.parse(localStorage.getItem("offline-messages") || "[]");
      offlineMessages.push(payload);
      localStorage.setItem("offline-messages", JSON.stringify(offlineMessages));

      toast.info("Sem internet. Mensagem salva para envio automático quando a conexão voltar.", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
      });

      history.push("/messages", { selected: location?.state?.selected });
      return;
    }

    createSolicitation(payload)
      .then((response) => {
        if (!response)
          throw new Error("Erro ao enviar mensagem");

        toast.success("Mensagem enviada com sucesso", {
          position: "top-right",
          autoClose: 3000, // autoclose após 3s
          hideProgressBar: false,
        });
        history.push("/messages", { selected: location?.state?.selected });

      })
      .catch((error: any) => {

        toast.error(error.message || "Erro ao enviar mensagem", {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
        });
        setIsSending(false);
      });
  };

  return (
    <>
      <CameraFullscreen
        open={cameraOpen}
        onClose={() => setCameraOpen(false)}
        onCapture={(image: any) => {
          if (image?.uri && image.uri.startsWith("blob:")) {
            // Converte o Blob URI para base64
            fetch(image.uri)
              .then((res) => res.blob())
              .then((blob) => {
                const reader = new FileReader();

                reader.onloadend = () => {
                  let base64 = reader.result as string;

                  // Verifica se o base64 já contém o prefixo "data:image/*;base64,"
                  if (base64.startsWith("data:")) {
                    base64 = base64.split(",")[1]; // Remove o prefixo "data:image/*;base64,"
                  }

                  const extension = image.name.split(".").pop() || "jpg"; // Pega a extensão
                  const mimeType = image.type || `image/${extension}`; // Usa o MIME type original ou gera com base na extensão
                  const uniqueName = `${image?.name ||
                    `${Date.now()}-${Math.random()
                      .toString(36)
                      .substring(2, 15)}.${extension}`
                    }`;
                  const uri = `file:///data/user/0/com.ideia.playbpo/cache/WebPicker/${uniqueName}`;

                  // Agora salvamos o base64 sem o prefixo
                  setFiles((prev) => [
                    ...prev,
                    {
                      name: uniqueName, // Nome único gerado
                      originalName: image.name,
                      uri, // URI simulada
                      type: mimeType,
                      base64, // Base64 sem o prefixo "data:image/*;base64,"
                      editing: false,
                    },
                  ]);
                };

                reader.readAsDataURL(blob); // Converte o Blob em base64
              })
              .catch((error) => {
                console.error("Erro ao buscar o Blob URI:", error);
              });
          } else {
            // Caso seja outro tipo de URI (base64 já incluído, por exemplo)
            setFiles((prev) => [
              ...prev,
              {
                name: image.name,
                originalName: image.name,
                uri: image.uri,
                type: image.type,
                base64: image?.base64, // Base64 já incluso no caso do arquivo ser base64
                editing: false,
              },
            ]);
          }
        }}
      />
      <div className="min-h-screen bg-primary-500">
        <div className="bg-primary-500 pt-4 pb-4 px-4">
          <div className="flex items-center justify-between max-w-2xl mx-auto">
            <div className="flex items-center w-full">
              <Button
                isIconOnly
                variant="light"
                onPress={() => {
                  history.push("/messages", {
                    selected: location?.state?.selected,
                  });
                }}
              >
                <Icon icon="lucide:arrow-left" className="text-secondary text-xl" />
              </Button>
              <div className="flex items-center flex-1">
                {/* <div className="rounded-full w-12 h-12 border border-white mr-2">
                  <img
                    src={selectedCompany?.file?.url || theme.logo.main}
                    alt="Logo"
                    className="p-[8px] object-cover"
                  />
                </div> */}
                <div className="rounded-full w-12 h-12 border border-white flex items-center justify-center overflow-hidden mr-2">
                  <img
                    src={getLogoBase64()}
                    alt="Logo"
                    className="max-w-full max-h-full object-contain" // Mantém proporção e tamanho natural
                  />
                </div>
                <div className="flex flex-col flex-1">
                  <span className="text-secondary text-base font-bold">
                    {authUser?.contato?.nome}
                  </span>
                  <span className="text-secondary text-sm font-semibold flex items-center max-w-full overflow-hidden">
                    <span className="max-w-[30%] truncate mr-2">
                      {" "}
                      {selectedCompany?.nome}
                    </span>
                    <span className="text-secondary/70 mr-2">/</span>
                    <span className="text-secondary/70 whitespace-nowrap">
                      {" "}
                      Nova Mensagem
                    </span>
                  </span>
                </div>
              </div>
              {/* <div className="w-10"></div> */}
            </div>
          </div>
        </div>

        <div className="bg-white rounded-t-3xl flex-1 min-h-[85vh] p-4">
          <div className="max-w-2xl mx-auto">
            <Card>
              <CardBody className="flex flex-col gap-4">
                <h2 className="text-xl font-semibold">
                  {selectedCompany?.nome}
                </h2>

                <Textarea
                  label="Mensagem"
                  placeholder="Digite sua mensagem"
                  value={message}
                  onValueChange={handleMessage}
                  minRows={18}
                // onFocus={handleMessageFocus}
                />

                <div className="flex gap-4 mt-4">
                  <Button
                    variant="flat"
                    startContent={<Icon icon="lucide:image" />}
                    onPress={handleGaleria}
                  >
                    Galeria
                  </Button>
                  <Button
                    variant="flat"
                    startContent={<Icon icon="lucide:camera" />}
                    onPress={handleCamera}
                  >
                    Camera
                  </Button>
                  <Button
                    variant="flat"
                    startContent={<Icon icon="lucide:file" />}
                    onPress={handleArquivo}
                  >
                    Arquivo
                  </Button>

                  <input
                    type="file"
                    ref={fileInputRef}
                    style={{ display: "none" }}
                    accept=".pdf,.doc,.docx,.txt,.xlsx,.xls,.ppt,.pptx,.csv,.json,.xml,.zip,.rar,.7z,.html,.odt,.ods,.odp,.rtf,.md"
                    multiple
                    onChange={(e) => {
                      const files = e.target.files;
                      if (files) {
                        Array.from(files).forEach((file) => {
                          const reader = new FileReader();
                          reader.onloadend = () => {
                            const dataUrl = reader.result as string;
                            const base64 = dataUrl.split(",")[1]; // remove o "data:...;base64," da string
                            const extension = file.name.split(".").pop();
                            const mimeType =
                              file.type || `application/${extension}`;
                            const uniqueName = `${Date.now()}-${Math.random()
                              .toString(36)
                              .substring(2, 15)}.${extension}`;
                            const uri = `file:///data/user/0/com.ideia.playbpo/cache/WebPicker/${uniqueName}`;

                            setFiles((prev) => [
                              ...prev,
                              {
                                name: uniqueName,
                                originalName: file.name,
                                uri,
                                type: mimeType,
                                base64,
                                editing: false,
                              },
                            ]);

                            console.log("Arquivo processado:", uniqueName);
                          };
                          reader.readAsDataURL(file);
                        });
                      }
                    }}
                  />
                  <input
                    type="file"
                    ref={imageInputRef}
                    style={{ display: "none" }}
                    accept="image/*,video/*"
                    multiple
                    onChange={(e) => {
                      const files = e.target.files;
                      if (files) {
                        Array.from(files).forEach((file) => {
                          const reader = new FileReader();
                          reader.onloadend = () => {
                            const dataUrl = reader.result as string;
                            const base64 = dataUrl.split(",")[1]; // remove prefixo data:<type>;base64,
                            const extension = file.name.split(".").pop();
                            const mimeType =
                              file.type ||
                              (file.type.startsWith("image/")
                                ? `image/${extension}`
                                : `video/${extension}`);
                            const uniqueName = `${Date.now()}-${Math.random()
                              .toString(36)
                              .substring(2, 15)}.${extension}`;
                            const uri = `file:///data/user/0/com.ideia.playbpo/cache/WebPicker/${uniqueName}`;

                            const type = file.type.startsWith("image/")
                              ? "image"
                              : "video";

                            setFiles((prev) => [
                              ...prev,
                              {
                                name: uniqueName,
                                originalName: file.name,
                                uri,
                                type,
                                mimeType,
                                base64,
                                editing: false,
                              },
                            ]);
                          };
                          reader.readAsDataURL(file);
                        });
                      }
                    }}
                  />
                </div>

                {files.length > 0 && (
                  <div className="mt-4 space-y-2">
                    <h3 className="text-lg font-semibold">
                      Arquivos Selecionados:
                    </h3>
                    <div className="max-h-[calc(100vh-600px)] overflow-y-auto">
                      {files.map((file, index) => (
                        <div
                          key={index}
                          className="flex items-center gap-2 h-10 bg-gray-50 rounded"
                        >
                          <div className="flex items-center justify-between w-full h-full">
                            <div className="flex items-center gap-2 flex-1 h-full">
                              <Icon
                                icon={`lucide:${file.type === "image"
                                  ? "image"
                                  : file.type === "video"
                                    ? "video"
                                    : "file-text"
                                  }`}
                                className="text-xl"
                              />
                              {(file.type === "image" ||
                                file.type === "image/jpeg") &&
                                (file as any).base64 && (
                                  <img
                                    src={`data:${(file as any).mimeType
                                      };base64,${(file as any).base64}`}
                                    alt={file.name}
                                    className="w-8 h-8 object-cover rounded ml-2"
                                  />
                                )}
                              {file.editing ? (
                                <div className="flex-1">
                                  <div className="flex w-full">
                                    <input
                                      type="text"
                                      value={
                                        file.tempName
                                          ? file.tempName.substring(
                                            0,
                                            file.tempName.lastIndexOf(".")
                                          )
                                          : file.name
                                            ? file.name.substring(
                                              0,
                                              file.name.lastIndexOf(".") !== -1
                                                ? file.name.lastIndexOf(".")
                                                : file.name.length
                                            )
                                            : ""
                                      }
                                      onChange={(e) => {
                                        // Extract file extension from the original name
                                        const fileNameParts =
                                          file.name.split(".");
                                        const extension =
                                          fileNameParts.length > 1
                                            ? "." +
                                            fileNameParts[
                                            fileNameParts.length - 1
                                            ]
                                            : "";

                                        // Atualiza o arquivo com o novo nome + extensão original
                                        setFiles((prev) =>
                                          prev.map((f) =>
                                            f === file
                                              ? {
                                                ...f,
                                                tempName:
                                                  e.target.value + extension,
                                              }
                                              : f
                                          )
                                        );
                                      }}
                                      className="flex-1 bg-white border border-gray-300 rounded-l px-2 py-1 text-sm h-8 outline-none"
                                    />
                                    <span className="bg-gray-100 border border-gray-300 border-l-0 rounded-r px-2 py-1 text-sm h-8 flex items-center">
                                      {file.name.includes(".")
                                        ? file.name.substring(
                                          file.name.lastIndexOf(".")
                                        )
                                        : ""}
                                    </span>
                                  </div>
                                </div>
                              ) : (
                                <div
                                  className="flex items-center gap-1"
                                  onClick={() =>
                                    setFiles((prev) =>
                                      prev.map((f) =>
                                        f === file ? { ...f, editing: true } : f
                                      )
                                    )
                                  }
                                >
                                  <span className="text-sm">{file.name}</span>
                                </div>
                              )}
                            </div>
                            <div className="flex items-center">
                              {file.editing ? (
                                <>
                                  <button
                                    onClick={() =>
                                      setFiles((prev) => {
                                        const updatedFiles = prev.map((f) => {
                                          if (f === file) {
                                            // Verifica se tempName existe e tem conteúdo antes da extensão
                                            const hasValidName = f.tempName
                                              ?.split(".")?.[0]
                                              ?.trim();

                                            return {
                                              ...f,
                                              editing: false,
                                              name:
                                                hasValidName && f.tempName
                                                  ? f.tempName
                                                  : f.name,
                                              tempName: undefined,
                                            };
                                          }
                                          return f;
                                        });
                                        return [...updatedFiles];
                                      })
                                    }
                                    className="p-1 text-green-500 hover:text-green-700"
                                  >
                                    <Icon
                                      icon="lucide:check"
                                      className="text-sm"
                                    />
                                  </button>
                                </>
                              ) : (
                                <>
                                  <button
                                    onClick={() =>
                                      setFiles((prev) =>
                                        prev.filter((f) => f !== file)
                                      )
                                    }
                                    className="p-1 text-gray-500 hover:text-gray-700"
                                  >
                                    <Icon
                                      icon="lucide:trash"
                                      className="text-sm"
                                    />
                                  </button>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardBody>
            </Card>
          </div>
        </div>

        <Button
          color="primary"
          size="lg"
          className="fixed bottom-safe-6 right-6 rounded-full shadow-lg border-4 border-secondary text-secondary flex items-center"
          startContent={<Icon icon="lucide:send" className="text-xl mr-2" />}
          onPress={sendMessage}
        >
          Enviar
        </Button>
      </div>
    </>
  );
}
